import { nextTick, onBeforeUnmount, onMounted, ref, type Ref } from "vue";

interface UseCollapsibleDescriptionOptions {
  contentRef: Ref<HTMLElement | null>;
}

export const useCollapsibleDescription = ({ contentRef }: UseCollapsibleDescriptionOptions) => {
  const isExpanded = ref(false);
  const canToggle = ref(false);
  let resizeObserver: ResizeObserver | null = null;

  const refreshCanToggle = async () => {
    await nextTick();

    const element = contentRef.value;
    if (!element) {
      canToggle.value = false;
      isExpanded.value = false;
      return;
    }

    if (isExpanded.value && canToggle.value) return;

    canToggle.value = element.scrollHeight > element.clientHeight;
    if (!canToggle.value) {
      isExpanded.value = false;
    }
  };

  const observeContentResize = () => {
    const element = contentRef.value;
    if (!element || typeof ResizeObserver === "undefined") return;

    resizeObserver = new ResizeObserver(() => {
      void refreshCanToggle();
    });
    resizeObserver.observe(element);
  };

  const toggle = () => {
    if (!canToggle.value) return;
    isExpanded.value = !isExpanded.value;
    if (!isExpanded.value) {
      void refreshCanToggle();
    }
  };

  onMounted(async () => {
    await refreshCanToggle();
    observeContentResize();
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
  });

  return {
    isExpanded,
    canToggle,
    toggle,
  };
};
